import { LanguageCodes, TEIComplexity, TemplateID } from '../../shared/enums';
import { Population } from '../../shared/interfaces';
import {
  learningTargets,
  TEST_LEARNING_TARGET_TYPES,
} from '../learning-targets';
import { TEST_HINT_TYPES, hints } from '../hints';
import { TEST_IMAGE_TYPES, images } from '../images';
import {
  TEST_TEXTS_TYPES,
  TEST_TITLE_TEXTS_TYPES,
  texts,
  titles,
} from '../texts';
import { TEST_CHOICE_TYPES } from '../choice-types';
import { TestAssociatedSuccessCriteria } from '../test-associated-success-criteria.enum';
import { multiChoiceQuestions } from '../multi-choice-questions';
import { multiChoiceImageQuestions } from '../multi-choice-image-questions';
import { multiSelectQuestions } from '../multi-select-questions';
import { multiSelectImageQuestions } from '../multi-select-image-questions';
import { inlineChoiceQuestions } from '../inline-choice-questions';
import { textEntryQuestions } from '../text-entry-questions';
import { constructedResponseQuestions } from '../constructed-response-questions';
import { extendedConstructedResponseQuestions } from '../extended-constructed-response-questions';
import { dynamicHorizontalSequencing } from '../dynamic-horizontal-sequencing';
import { staticHorizontalSequencing } from '../static-horizontal-sequencing';
import { staticVerticalSequencing } from '../static-vertical-sequencing';
import { fillInTheBlankTileText } from '../fill-in-the-blank-tile-text-questions';
import { sortingBuckets } from '../sorting-buckets';
import { TEST_POPULATION_TYPES, copyHints } from '.';
import { dndGrid } from '../dnd-grid';
import { hotTextQuestions } from '../hot-text-questions';
import { dndGridMatch } from '../dnd-grid-match';

/*****************************************************************/
/* READING POPULATIONS                                           */
/*****************************************************************/

const constReadingPopParams = {
  product: 'Reading',
  language: LanguageCodes.ENGLISH,
};

const constReadingPopParamsMC = {
  templateID: TemplateID.MULTIPLE_CHOICE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.MULTI_CHOICE],
  domain: { text: 'Multichoice domain' },
  standard: { text: 'Multichoice standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.MULTI_CHOICE]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.MC',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.MC',
  },
  ...constReadingPopParams,
};

export const populationsReadingMC: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_MC_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_SHORT],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_MEDIUM],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_LONG],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_SHORT}`,
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_MEDIUM}`,
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_LONG}`,
    instructions: {
      text: 'This passage has instructions about something that you should read about here.',
    },
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_SHORT_WITH_TITLE]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_SHORT_WITH_TITLE}`,
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    passageTitle: titles[TEST_TITLE_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_MEDIUM_WITH_TITLE]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_MEDIUM_WITH_TITLE}`,
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    passageTitle: titles[TEST_TITLE_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_LONG_WITH_TITLE]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_LONG_WITH_TITLE}`,
    instructions: {
      text: 'This passage has instructions about something that you should read about here.',
    },
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    passageTitle: titles[TEST_TITLE_TEXTS_TYPES.LONG],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_TITLE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_TITLE_SHORT}`,
    populationData: {
      ...multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_SHORT],
      question: {
        text: 'This TEI has a passage title, but does not have a passage, so it will not be displayed',
      },
    },
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    passageTitle: titles[TEST_TITLE_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_TITLE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_TITLE_MEDIUM}`,
    populationData: {
      ...multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_MEDIUM],
      question: {
        text: 'This TEI has a passage title, but does not have a passage, so it will not be displayed',
      },
    },
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passageTitle: titles[TEST_TITLE_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_TITLE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_TITLE_LONG}`,
    populationData: {
      ...multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_LONG],
      question: {
        text: 'This TEI has a passage title, but does not have a passage, so it will not be displayed',
      },
    },
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    passageTitle: titles[TEST_TITLE_TEXTS_TYPES.LONG],
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_TRUE_FALSE]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_TRUE_FALSE}`,
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    instructions: {
      text: 'This is instruction text for the true or false question type.',
    },
    populationData:
      multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_TRUE_FALSE],
    ...constReadingPopParamsMC,
    templateID: TemplateID.TRUE_FALSE_CHOICE,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_TRUE_FALSE]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_TRUE_FALSE}`,
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    instructions: {
      text: 'This is instruction text for the true or false question type.',
    },
    populationData:
      multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_TRUE_FALSE],
    ...constReadingPopParamsMC,
    templateID: TemplateID.TRUE_FALSE_CHOICE,
  },
};

const constReadingPopParamsMCI = {
  templateID: TemplateID.MULTIPLE_CHOICE_IMAGE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.MULTI_CHOICE_IMAGE],
  domain: { text: 'Multichoice image domain' },
  standard: { text: 'Multichoice image standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.MULTI_CHOICE]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.MCI',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.MCI',
  },
  ...constReadingPopParams,
};

export const populationsReadingMCI: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_MCI_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MCI_SHORT}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_R_MCI_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    ...constReadingPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MCI_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MCI_MEDIUM}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_R_MCI_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    ...constReadingPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MCI_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MCI_LONG}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_R_MCI_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    ...constReadingPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MCI_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MCI_PASSAGE_SHORT}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_R_MCI_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MCI_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MCI_PASSAGE_MEDIUM}`,
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_R_MCI_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MCI_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MCI_PASSAGE_LONG}`,
    instructions: {
      text: 'This passage has instructions about something that you should read about here.',
    },
    populationData:
      multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_R_MCI_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constReadingPopParamsMCI,
  },
};

const constReadingPopParamsMS = {
  templateID: TemplateID.MULTI_SELECT,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.MULTI_SELECT],
  domain: { text: 'Multiselect domain' },
  standard: { text: 'Multiselect standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.MULTI_SELECT]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.MS',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.MS',
  },
  ...constReadingPopParams,
};

export const populationsReadingMS: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_MS_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MS_SHORT}`,
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_R_MS_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    ...constReadingPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_R_MS_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MS_MEDIUM}`,
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_R_MS_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    ...constReadingPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_R_MS_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MS_LONG}`,
    instructions: {
      text: 'This passage has instructions about something that you should read about here.',
    },
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_R_MS_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    ...constReadingPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_R_MS_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MS_PASSAGE_SHORT}`,
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_R_MS_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_R_MS_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MS_PASSAGE_MEDIUM}`,
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_R_MS_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_R_MS_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MS_PASSAGE_LONG}`,
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData: multiSelectQuestions[TEST_CHOICE_TYPES.TEST_R_MS_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constReadingPopParamsMS,
  },
};

const constReadingPopParamsMSI = {
  templateID: TemplateID.MULTI_SELECT_IMAGE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.MULTI_SELECT_IMAGE],
  domain: { text: 'Multiselect image domain' },
  standard: { text: 'Multiselect image standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.MULTI_SELECT]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.MSI',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.MSI',
  },
  ...constReadingPopParams,
};

export const populationsReadingMSI: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_MSI_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MSI_SHORT}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    ...constReadingPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MSI_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MSI_MEDIUM}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    ...constReadingPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MSI_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MSI_LONG}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    ...constReadingPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MSI_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MSI_PASSAGE_SHORT}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_SHORT],
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MSI_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MSI_PASSAGE_MEDIUM}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_MEDIUM],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MSI_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MSI_PASSAGE_LONG}`,
    populationData:
      multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    passage: texts[TEST_TEXTS_TYPES.LONG],
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constReadingPopParamsMSI,
  },
};

const constReadingPopParamsIC = {
  templateID: TemplateID.INLINE_CHOICE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.INLINE_CHOICE],
  domain: { text: 'Inline choice domain' },
  standard: { text: 'Inline choice standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.INLINE_CHOICE]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.IC',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.IC',
  },
  ...constReadingPopParams,
};

export const populationsReadingIC: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_IC_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_IC_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: inlineChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_IC_SHORT],
    ...constReadingPopParamsIC,
  },
  [TEST_POPULATION_TYPES.TEST_R_IC_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_IC_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: inlineChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_IC_MEDIUM],
    ...constReadingPopParamsIC,
  },
  [TEST_POPULATION_TYPES.TEST_R_IC_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_IC_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData: inlineChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_IC_LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constReadingPopParamsIC,
  },
};

const constReadingPopParamsTE = {
  templateID: TemplateID.TEXT_ENTRY,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.TEXT_ENTRY],
  domain: { text: 'Text entry domain' },
  standard: { text: 'Text entry standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.TEXT_ENTRY]],
  feedbackFirstIncorrect: {
    content: 'Insert first incorrect feedback - Test.R.TE',
  },
  feedbackSecondIncorrect: {
    content: 'Insert second incorrect feedback - Test.R.TE',
  },
  ...constReadingPopParams,
};

export const populationsReadingTE: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_TE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_TE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: textEntryQuestions[TEST_CHOICE_TYPES.TEST_R_TE_SHORT],
    ...constReadingPopParamsTE,
  },
  [TEST_POPULATION_TYPES.TEST_R_TE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_TE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: textEntryQuestions[TEST_CHOICE_TYPES.TEST_R_TE_MEDIUM],
    ...constReadingPopParamsTE,
  },
  [TEST_POPULATION_TYPES.TEST_R_TE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_TE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData: textEntryQuestions[TEST_CHOICE_TYPES.TEST_R_TE_LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constReadingPopParamsTE,
  },
};

const constReadingPopParamsSCR = {
  templateID: TemplateID.CONSTRUCTED_RESPONSE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.SHORT_CONST_RESPONSE,
  ],
  domain: { text: 'Short Constructed Response domain' },
  standard: { text: 'Short Constructed Response standard' },
  learningTarget: [
    learningTargets[TEST_LEARNING_TARGET_TYPES.SHORT_CONSTRUCTED_RESPONSE],
  ],
  ...constReadingPopParams,
};

const constReadingPopParamsSCRR = {
  templateID: TemplateID.CONSTRUCTED_RESPONSE_WITH_RUBRIC,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.SHORT_CONST_RESPONSE_RUBRIC,
  ],
  domain: { text: 'Short Constructed Response Rubric domain' },
  standard: { text: 'Short Constructed Response Rubric standard' },
  learningTarget: [
    learningTargets[
      TEST_LEARNING_TARGET_TYPES.SHORT_CONSTRUCTED_RESPONSE_WITH_RUBRIC
    ],
  ],
  ...constReadingPopParams,
};

export const populationsReadingSCR: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_SCR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCR_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_SHORT],
    ...constReadingPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCR_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_MEDIUM],
    ...constReadingPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_LONG],
    ...constReadingPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_MEDIUM],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    ...constReadingPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCRR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCRR_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_SHORT],
    ...constReadingPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCRR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCRR_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_MEDIUM],
    ...constReadingPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCRR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCRR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_LONG],
    ...constReadingPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCRR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCRR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCRR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCRR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_MEDIUM],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_SCRR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_SCRR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constReadingPopParamsSCRR,
  },
};

const constReadingPopParamsECR = {
  templateID: TemplateID.EXTENDED_CONSTRUCTED_RESPONSE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: ['extendedconstructedresponse'],
  domain: { text: 'Extended Constructed Response domain' },
  standard: { text: 'Extended Constructed Response standard' },
  learningTarget: [
    learningTargets[TEST_LEARNING_TARGET_TYPES.EXTENDED_CONSTRUCTED_RESPONSE],
  ],
  ...constReadingPopParams,
};

const constReadingPopParamsECRR = {
  templateID: TemplateID.EXTENDED_CONSTRUCTED_RESPONSE_WITH_RUBRIC,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: ['extendedconstructedresponserubric'],
  domain: { text: 'Extended Constructed Response Rubric domain' },
  standard: { text: 'Extended Constructed Response Rubric standard' },
  learningTarget: [
    learningTargets[
      TEST_LEARNING_TARGET_TYPES.EXTENDED_CONSTRUCTED_RESPONSE_WITH_RUBRIC
    ],
  ],
  ...constReadingPopParams,
};

export const populationsReadingECR: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_ECR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECR_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_SHORT],
    ...constReadingPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECR_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_MEDIUM],
    ...constReadingPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_LONG],
    ...constReadingPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_MEDIUM],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    ...constReadingPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECRR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECRR_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECRR_SHORT],
    ...constReadingPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECRR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECRR_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[
        TEST_CHOICE_TYPES.TEST_R_ECRR_MEDIUM
      ],
    ...constReadingPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECRR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECRR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECRR_LONG],
    ...constReadingPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECRR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECRR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECRR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constReadingPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECRR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECRR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[
        TEST_CHOICE_TYPES.TEST_R_ECRR_MEDIUM
      ],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constReadingPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_R_ECRR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_ECRR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constReadingPopParamsECRR,
  },
};

export const populationsReadingDNDSandbox: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_M_DND_STATIC_HORIZ_SEQ]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_STATIC_HORIZ_SEQ}`,
    language: LanguageCodes.ENGLISH,
    product: 'Reading',
    associatedSuccessCriteria: ['tag1', 'tag2'],
    complexity: 'Comprehension',
    templateID: TemplateID.STATIC_HORIZONTAL_SEQUENCING,
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    instructions: {
      text: 'Drag the items into the correct order.',
    },
    populationData: staticHorizontalSequencing['test1'],
    feedbackFirstIncorrect: { content: 'You have 1 more try.' },
    feedbackSecondIncorrect: { content: 'The correct answer is...' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_DYNAMIC_HORIZ_SEQ]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_DYNAMIC_HORIZ_SEQ}`,
    language: LanguageCodes.ENGLISH,
    product: 'Reading',
    associatedSuccessCriteria: ['tag1', 'tag2'],
    complexity: 'Comprehension',
    templateID: TemplateID.DYNAMIC_HORIZONTAL_SEQUENCING,
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    instructions: {
      text: 'Drag the items into the correct order.',
    },
    populationData: dynamicHorizontalSequencing['test1'],
    feedbackFirstIncorrect: { content: 'You have 1 more try.' },
    feedbackSecondIncorrect: { content: 'The correct answer is...' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_STATIC_VERT_SEQ]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_STATIC_VERT_SEQ}`,
    language: LanguageCodes.ENGLISH,
    product: 'Reading',
    associatedSuccessCriteria: ['tag1', 'tag2'],
    complexity: 'Comprehension',
    templateID: TemplateID.STATIC_VERTICAL_SEQUENCING,
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    instructions: {
      text: 'Drag the items into the correct order.',
    },
    populationData: staticVerticalSequencing['test1'],
    feedbackFirstIncorrect: { content: 'You have 1 more try.' },
    feedbackSecondIncorrect: { content: 'The correct answer is...' },
  },
};

const constReadingPopParamsDND = {
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.DRAG_AND_DROP],
  domain: { text: 'Drag and Drop domain' },
  standard: { text: 'Drag and Drop standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.DRAG_AND_DROP]],
  feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
  feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  ...constReadingPopParams,
};

export const populationsReadingDND: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_DND_BLANK_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_BLANK_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      fillInTheBlankTileText[TEST_CHOICE_TYPES.TEST_R_DND_BLANK_SHORT],
    templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_BLANK_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_BLANK_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      fillInTheBlankTileText[TEST_CHOICE_TYPES.TEST_R_DND_BLANK_LONG],
    templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_SEQ_STATIC_HORZ_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_SEQ_STATIC_HORZ_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      staticHorizontalSequencing[TEST_CHOICE_TYPES.TEST_R_DND_HORZ_SEQ_SHORT],
    templateID: TemplateID.STATIC_HORIZONTAL_SEQUENCING,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_SEQ_STATIC_HORZ_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_SEQ_STATIC_HORZ_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      staticHorizontalSequencing[TEST_CHOICE_TYPES.TEST_R_DND_HORZ_SEQ_LONG],
    templateID: TemplateID.STATIC_HORIZONTAL_SEQUENCING,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_SEQ_STATIC_VERT_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_SEQ_STATIC_VERT_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      staticVerticalSequencing[TEST_CHOICE_TYPES.TEST_R_DND_VERT_SEQ_SHORT],
    templateID: TemplateID.STATIC_VERTICAL_SEQUENCING,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_SEQ_STATIC_VERT_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_SEQ_STATIC_VERT_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      staticVerticalSequencing[TEST_CHOICE_TYPES.TEST_R_DND_VERT_SEQ_LONG],
    templateID: TemplateID.STATIC_VERTICAL_SEQUENCING,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_SORT_BUCKETS_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_SORT_BUCKETS_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      sortingBuckets[TEST_CHOICE_TYPES.TEST_R_DND_SORT_BUCKETS_SHORT],
    templateID: TemplateID.SORTING_BUCKETS,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_SORT_BUCKETS_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_SORT_BUCKETS_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      sortingBuckets[TEST_CHOICE_TYPES.TEST_R_DND_SORT_BUCKETS_LONG],
    templateID: TemplateID.SORTING_BUCKETS,
    ...constReadingPopParamsDND,
  },
};

export const populationsOverridenTries: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_MC_MEDIUM_1_TRY]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_MEDIUM_1_TRY}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: {
      ...multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_MEDIUM],
      tries: 1,
    },
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MCI_MEDIUM_2_TRY]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MCI_MEDIUM_2_TRY}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: {
      ...multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_R_MCI_MEDIUM],
      tries: 2,
    },
    ...constReadingPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MS_MEDIUM_3_TRY]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MS_MEDIUM_3_TRY}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: {
      ...multiSelectQuestions[TEST_CHOICE_TYPES.TEST_R_MS_MEDIUM],
      tries: 3,
    },
    ...constReadingPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_R_MSI_MEDIUM_1_TRY]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MSI_MEDIUM_1_TRY}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: {
      ...multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_MEDIUM],
      tries: 1,
    },
    ...constReadingPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_R_TE_MEDIUM_2_TRY]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_TE_MEDIUM_2_TRY}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: {
      ...textEntryQuestions[TEST_CHOICE_TYPES.TEST_R_TE_MEDIUM],
      tries: 2,
    },
    ...constReadingPopParamsTE,
  },
  [TEST_POPULATION_TYPES.TEST_R_IC_MEDIUM_3_TRY]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_IC_MEDIUM_3_TRY}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: {
      ...inlineChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_IC_MEDIUM],
      tries: 3,
    },
    ...constReadingPopParamsIC,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_BLANK_SHORT_1_TRY]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_BLANK_SHORT_1_TRY}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: {
      ...fillInTheBlankTileText[TEST_CHOICE_TYPES.TEST_R_DND_BLANK_SHORT],
      tries: 2,
    },
    templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_SORT_BUCKETS_SHORT_4_TRY]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_SORT_BUCKETS_SHORT_4_TRY}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData: {
      ...sortingBuckets[TEST_CHOICE_TYPES.TEST_R_DND_SORT_BUCKETS_SHORT],
      tries: 4,
    },
    templateID: TemplateID.SORTING_BUCKETS,
    ...constReadingPopParamsDND,
  },
};

export const constReadingPopParamsDNDGrid = {
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.DRAG_AND_DROP],
  domain: { text: 'Drag and Drop Grid domain' },
  standard: { text: 'Drag and Drop Grid standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.DRAG_AND_DROP]],
  feedbackFirstIncorrect: {
    content: `Almost! Try again. How many sounds are in the word - {TARGET_TEXT}?`,
  },
  feedbackSecondIncorrect: {
    content: `Nice Try! Let's do this one together. The word {TARGET_TEXT} has four sounds. {TARGET_TEXT_PHO} - Once again. The sounds are {TARGET_TEXT_PHO}`,
  },
  ...constReadingPopParams,
};

export const populationsReadingDNDGrid: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_DND_GRID_1X6X4]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_GRID_1X6X4}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT_TARGET_TEXT]),
    instructions: {
      text: 'Drag the correct number of dots to represent the number of sounds. (verbal)',
      displayText:
        'Drag the correct number of dots to represent the number of sounds (visual).',
    },
    populationData: dndGrid[TEST_CHOICE_TYPES.TEST_R_DND_GRID_1X6X4],
    templateID: TemplateID.DND_GRID,
    verbal: true,
    targetText: 'saying',
    ...constReadingPopParamsDNDGrid,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_GRID_1X5X3]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_GRID_1X5X3}`,
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_SHORT_TARGET_TEXT]),
    instructions: {
      text: 'Drag the correct number of dots to represent the number of sounds. (verbal)',
      displayText:
        'Drag the correct number of dots to represent the number of sounds (visual).',
    },
    populationData: dndGrid[TEST_CHOICE_TYPES.TEST_R_DND_GRID_1X5X3],
    templateID: TemplateID.DND_GRID,
    verbal: true,
    targetText: 'said',
    ...constReadingPopParamsDNDGrid,
  },
};

export const constReadingPopParamsDNDGridMatch = {
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.DRAG_AND_DROP],
  domain: { text: 'Drag and Drop Grid Match domain' },
  standard: { text: 'Drag and Drop Grid Match standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.DRAG_AND_DROP]],
  feedbackFirstIncorrect: {
    content: `Almost! Try again. Spell the word - {TARGET_TEXT}`,
  },
  feedbackSecondIncorrect: {
    content: `Nice Try! Say it with me! {TARGET_TEXT_PHO}`,
  },
  ...constReadingPopParams,
};

export const populationsReadingDNDGridMatch: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_DND_GRID_MATCH_2X6X4]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_GRID_MATCH_2X6X4}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT_TARGET_TEXT]),
    instructions: {
      text: 'Drag the letters to spell the word {TARGET_TEXT}. (verbal)',
      displayText: 'Drag the letters to spell the word {TARGET_TEXT} (visual).',
    },
    populationData: dndGridMatch[TEST_CHOICE_TYPES.TEST_R_DND_GRID_MATCH_2X6X4],
    templateID: TemplateID.DND_GRID_MATCH,
    verbal: true,
    targetText: 'test',
    ...constReadingPopParamsDNDGridMatch,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_GRID_MATCH_1X5X3]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_GRID_MATCH_1X5X3}`,
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_SHORT_TARGET_TEXT]),
    instructions: {
      text: 'Drag the letters to spell the word {TARGET_TEXT}. (verbal)',
      displayText: 'Drag the letters to spell the word {TARGET_TEXT} (visual).',
    },
    populationData: dndGridMatch[TEST_CHOICE_TYPES.TEST_R_DND_GRID_MATCH_1X5X3],
    templateID: TemplateID.DND_GRID_MATCH,
    verbal: true,
    targetText: 'thought',
    ...constReadingPopParamsDNDGridMatch,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_GRID_MATCH_2X5X10]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_GRID_MATCH_2X5X10}`,
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_SHORT_TARGET_TEXT]),
    instructions: {
      text: 'Drag the letters to spell the word {TARGET_TEXT}. (verbal)',
      displayText: 'Drag the letters to spell the word {TARGET_TEXT} (visual).',
    },
    populationData:
      dndGridMatch[TEST_CHOICE_TYPES.TEST_R_DND_GRID_MATCH_2X5X10],
    templateID: TemplateID.DND_GRID_MATCH,
    verbal: true,
    targetText: 'instrument',
    ...constReadingPopParamsDNDGridMatch,
  },
};

export const populationsReadingVerbal: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_DND_GRID_1X6X4]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_GRID_1X6X4}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT_TARGET_TEXT]),
    instructions: {
      text: 'Drag the correct number of dots to represent the number of sounds. (verbal)',
      displayText:
        'Drag the correct number of dots to represent the number of sounds (visual).',
    },
    populationData: {
      ...dndGrid[TEST_CHOICE_TYPES.TEST_R_DND_GRID_1X6X4],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.DRAG_AND_DROP,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    templateID: TemplateID.DND_GRID,
    verbal: true,
    targetText: 'saying',
    ...constReadingPopParamsDNDGrid,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_GRID_1X5X3]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_GRID_1X5X3}`,
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_SHORT_TARGET_TEXT]),
    instructions: {
      text: 'Drag the correct number of dots to represent the number of sounds. (verbal)',
      displayText:
        'Drag the correct number of dots to represent the number of sounds (visual).',
    },
    populationData: {
      ...dndGrid[TEST_CHOICE_TYPES.TEST_R_DND_GRID_1X5X3],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.DRAG_AND_DROP,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    templateID: TemplateID.DND_GRID,
    verbal: true,
    targetText: 'said',
    ...constReadingPopParamsDNDGrid,
  },
  [TEST_POPULATION_TYPES.TEST_R_MC_MEDIUM_VERBAL]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_MEDIUM_VERBAL}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: {
      ...multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_MEDIUM_VERBAL],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.MULTI_CHOICE,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    verbal: true,
    disableReadAloud: false,
    ...constReadingPopParamsMC,
  },
  [TEST_POPULATION_TYPES.TEST_R_MCI_MEDIUM_VERBAL]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MCI_MEDIUM_VERBAL}`,
    hint: copyHints(hints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    populationData: {
      ...multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_R_MCI_MEDIUM_VERBAL],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.MULTI_CHOICE_IMAGE,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    verbal: true,
    ...constReadingPopParamsMCI,
  },
  [TEST_POPULATION_TYPES.TEST_R_MS_MEDIUM_VERBAL]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MS_MEDIUM_VERBAL}`,
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    populationData: {
      ...multiSelectQuestions[TEST_CHOICE_TYPES.TEST_R_MS_MEDIUM_VERBAL],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.MULTI_SELECT,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    verbal: true,
    ...constReadingPopParamsMS,
  },
  [TEST_POPULATION_TYPES.TEST_R_MSI_MEDIUM_VERBAL]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MSI_MEDIUM_VERBAL}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: {
      ...multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_MEDIUM_VERBAL],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.MULTI_SELECT_IMAGE,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    verbal: true,
    ...constReadingPopParamsMSI,
  },
  [TEST_POPULATION_TYPES.TEST_R_TE_MEDIUM_VERBAL]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_TE_MEDIUM_VERBAL}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: {
      ...textEntryQuestions[TEST_CHOICE_TYPES.TEST_R_TE_MEDIUM_VERBAL],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.TEXT_ENTRY,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    verbal: true,
    ...constReadingPopParamsTE,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_BLANK_VERBAL]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_BLANK_VERBAL}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: {
      ...fillInTheBlankTileText[TEST_CHOICE_TYPES.TEST_R_DND_BLANK_VERBAL],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.DRAG_AND_DROP,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
    verbal: true,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_BLANK_VERBAL_WITH_VIDEO]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_BLANK_VERBAL_WITH_VIDEO}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: {
      ...fillInTheBlankTileText[
        TEST_CHOICE_TYPES.TEST_R_DND_BLANK_VERBAL_WITH_VIDEO
      ],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.DRAG_AND_DROP,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
    verbal: true,
    usePhonemeVideo: true,
    targetText: 'said',
    ...constReadingPopParamsDND,
    feedbackSecondIncorrect: {
      content: `Nice Try! Let's do this one together. The word {TARGET_TEXT} has four sounds.`,
    },
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_BLANK_VERBAL_AND_VIDEO_WITH_REPEAT_OPTIONS]:
    {
      id: `${TEST_POPULATION_TYPES.TEST_R_DND_BLANK_VERBAL_AND_VIDEO_WITH_REPEAT_OPTIONS}`,
      hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
      populationData: {
        ...fillInTheBlankTileText[
          TEST_CHOICE_TYPES.TEST_R_DND_BLANK_WITH_REPEAT_OPTIONS
        ],
        associatedSuccessCriteria: [
          TestAssociatedSuccessCriteria.DRAG_AND_DROP,
          TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
        ],
      },
      templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
      usePhonemeVideo: true,
      targetText: 'murmur',
      verbal: true,
      ...constReadingPopParamsDND,
      feedbackSecondIncorrect: {
        content: `Nice Try! Let's do this one together. The word {TARGET_TEXT} has four sounds. {TARGET_TEXT_PHO} - Once again. The sounds are {TARGET_TEXT_PHO}`,
      },
    },
  [TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_VERBAL]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_MC_PASSAGE_VERBAL}`,
    instructions: {
      text: 'This passage has instructions about something that you should read about here. (verbal)',
      displayText:
        'This passage has instructions about something that you should read about here. (visual)',
    },
    populationData: multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_LONG],
    hint: copyHints(hints[TEST_HINT_TYPES.MULTIPLE_MEDIUM]),
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    verbal: true,
    ...constReadingPopParamsMC,
  },
};

const constReadingPopParamsHotText = {
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.HOT_TEXT],
  domain: { text: 'Hot Text domain' },
  standard: { text: 'Hot Text standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.HOT_TEXT]],
  feedbackFirstIncorrect: {
    content: 'First Incorrect. First Hottext feedback.',
  },
  feedbackSecondIncorrect: {
    content: 'Second Incorrect. Second hottext feedback.',
  },
  ...constReadingPopParams,
};

export const populationsHotText: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_HOT_TEXT_ONE_WORD]: {
    id: `${TEST_POPULATION_TYPES.TEST_HOT_TEXT_ONE_WORD}`, //questionID, unique to this specific population
    templateID: TemplateID.HOT_TEXT, //TEI Type: Multiple choice, etc.
    populationData: hotTextQuestions[TEST_CHOICE_TYPES.TEST_HOT_TEXT_ONE_WORD], //question data
    hint: [...hints[TEST_HINT_TYPES.SINGLE_MEDIUM]],
    ...constReadingPopParamsHotText,
    feedbackFirstIncorrect: {
      content:
        'Good try! The word is said, try reading out loud and sounding out each word. Make sure to click on all instances of the word "said".',
    },
    feedbackSecondIncorrect: {
      content: 'Nice try! Here are all of the correct answers',
    },
    verbal: true,
  },
  [TEST_POPULATION_TYPES.TEST_HOT_TEXT_MULTIPLE_WORDS]: {
    id: `${TEST_POPULATION_TYPES.TEST_HOT_TEXT_MULTIPLE_WORDS}`, //questionID, unique to this specific population
    templateID: TemplateID.HOT_TEXT, //TEI Type: Multiple choice, etc.
    populationData:
      hotTextQuestions[TEST_CHOICE_TYPES.TEST_HOT_TEXT_MULTIPLE_WORDS], //question data
    hint: [...hints[TEST_HINT_TYPES.SINGLE_MEDIUM]],
    ...constReadingPopParamsHotText,
    verbal: true,
  },
  [TEST_POPULATION_TYPES.TEST_HOT_TEXT_ONE_PHRASE]: {
    id: `${TEST_POPULATION_TYPES.TEST_HOT_TEXT_ONE_PHRASE}`, //questionID, unique to this specific population
    templateID: TemplateID.HOT_TEXT, //TEI Type: Multiple choice, etc.
    populationData:
      hotTextQuestions[TEST_CHOICE_TYPES.TEST_HOT_TEXT_ONE_PHRASE], //question data
    hint: [...hints[TEST_HINT_TYPES.SINGLE_MEDIUM]],
    ...constReadingPopParamsHotText,
    verbal: false,
  },
  [TEST_POPULATION_TYPES.TEST_HOT_TEXT_MULTIPLE_PHRASES]: {
    id: `${TEST_POPULATION_TYPES.TEST_HOT_TEXT_MULTIPLE_PHRASES}`, //questionID, unique to this specific population
    templateID: TemplateID.HOT_TEXT, //TEI Type: Multiple choice, etc.
    populationData:
      hotTextQuestions[TEST_CHOICE_TYPES.TEST_HOT_TEXT_MULTIPLE_PHRASES], //question data
    hint: [...hints[TEST_HINT_TYPES.SINGLE_MEDIUM]],
    ...constReadingPopParamsHotText,
    verbal: true,
  },
  [TEST_POPULATION_TYPES.TEST_HOT_TEXT_MULTIPLE_WORDS_MULTIPLE_PHRASES]: {
    id: `${TEST_POPULATION_TYPES.TEST_HOT_TEXT_MULTIPLE_WORDS_MULTIPLE_PHRASES}`, //questionID, unique to this specific population
    templateID: TemplateID.HOT_TEXT, //TEI Type: Multiple choice, etc.
    populationData:
      hotTextQuestions[
        TEST_CHOICE_TYPES.TEST_HOT_TEXT_MULTIPLE_WORDS_MULTIPLE_PHRASES
      ], //question data
    hint: [...hints[TEST_HINT_TYPES.SINGLE_MEDIUM]],
    ...constReadingPopParamsHotText,
    verbal: false,
  },
};

export const populationsNonVerbalFITB: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_R_DND_BLANK_WITH_VIDEO]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_BLANK_WITH_VIDEO}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: {
      ...fillInTheBlankTileText[
        TEST_CHOICE_TYPES.TEST_R_DND_BLANK_VERBAL_WITH_VIDEO
      ],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.DRAG_AND_DROP,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
    usePhonemeVideo: true,
    targetText: 'said',
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_BLANK]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_BLANK}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: {
      ...fillInTheBlankTileText[TEST_CHOICE_TYPES.TEST_R_DND_BLANK_VERBAL],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.DRAG_AND_DROP,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_BLANK_WITH_REPEAT_OPTIONS]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_BLANK_WITH_REPEAT_OPTIONS}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: {
      ...fillInTheBlankTileText[
        TEST_CHOICE_TYPES.TEST_R_DND_BLANK_WITH_REPEAT_OPTIONS
      ],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.DRAG_AND_DROP,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
    targetText: 'murmur',
    ...constReadingPopParamsDND,
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_WITH_REPEAT_OPTIONS]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_WITH_REPEAT_OPTIONS}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData: {
      ...fillInTheBlankTileText[
        TEST_CHOICE_TYPES.TEST_R_DND_BLANK_WITH_REPEAT_OPTIONS
      ],
      associatedSuccessCriteria: [
        TestAssociatedSuccessCriteria.DRAG_AND_DROP,
        TestAssociatedSuccessCriteria.VERBAL_POPULATIONS,
      ],
    },
    templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
    targetText: 'murmur',
    ...constReadingPopParamsDND,
    feedbackSecondIncorrect: {
      content: `Nice Try! Let's do this one together. The word {TARGET_TEXT} has four sounds. {TARGET_TEXT_PHO} - Once again. The sounds are {TARGET_TEXT_PHO}`,
    },
  },
};
